/**
 * „vor 5 Minuten“, „gestern“, „in 3 Tagen“ — relative time in the reader's
 * locale via `Intl.RelativeTimeFormat`. Past a cut-off (`maxDays`) it reads
 * as a short absolute date instead, which is easier to place than
 * „vor 7 Monaten“.
 */

export type NxDateLike = Date | string | number | null | undefined;

export interface FormatRelativeTimeOptions {
  /** Reference point; defaults to the current time. */
  now?: Date | number;
  locale?: string;
  /** `long` „vor 5 Minuten“, `short` „vor 5 Min.“, `narrow` „vor 5 Min.“. */
  style?: Intl.RelativeTimeFormatStyle;
  /** Beyond this many days the absolute date is shown. Defaults to 30. */
  maxDays?: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** The date as a `Date`, or `null` when it cannot be read. */
export function toDate(value: NxDateLike): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whole days between two dates by calendar day (so „gestern“ means yesterday, not „24 h ago“). */
function calendarDays(date: Date, now: Date): number {
  const a = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / DAY);
}

/**
 * Relative time of `value` against `now`. Under a minute reads „jetzt“, then
 * minutes, hours (same day or within 12 h), calendar days up to `maxDays`,
 * then a short absolute date. Unreadable input gives `''`.
 */
export function formatRelativeTime(
  value: NxDateLike,
  options: FormatRelativeTimeOptions = {}
): string {
  const date = toDate(value);
  if (!date) return '';
  const now = options.now === undefined ? new Date() : new Date(options.now);
  const rtf = new Intl.RelativeTimeFormat(options.locale, {
    numeric: 'auto',
    style: options.style ?? 'long',
  });
  const diff = date.getTime() - now.getTime();
  const abs = Math.abs(diff);
  if (abs < MINUTE) return rtf.format(0, 'second');
  if (abs < HOUR) return rtf.format(Math.trunc(diff / MINUTE), 'minute');
  const days = calendarDays(date, now);
  if (days === 0 || abs < 12 * HOUR) return rtf.format(Math.trunc(diff / HOUR), 'hour');
  if (Math.abs(days) <= (options.maxDays ?? 30)) return rtf.format(days, 'day');
  return formatAbsolute(date, options.locale, date.getFullYear() !== now.getFullYear());
}

/** Short absolute date: „14. Aug.“, with the year when it is not this year. */
function formatAbsolute(date: Date, locale: string | undefined, withYear: boolean): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(date);
}

/** Full date and time for a tooltip: „23. September 2026 um 14:05“. */
export function formatDateTimeLong(value: NxDateLike, locale?: string): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeStyle: 'short' }).format(date);
}
